/**
 * Browser helpers when `NEXT_PUBLIC_API_BASE_URL` points at `/api/proxy`.
 * Auth is applied server-side from the NextAuth session cookie; clients must not
 * send Bearer on proxied routes (causes HTTP 431).
 */

export function isBrowserApiProxied(): boolean {
  const base = getBrowserApiBaseUrl();
  return (
    base === "/api/proxy" ||
    base.startsWith("/api/proxy/") ||
    base.endsWith("/api/proxy")
  );
}

/**
 * API base for browser requests.
 * When env is an absolute URL on another host (e.g. sales-assistant-apps.azurewebsites.net),
 * use same-origin `/api/proxy` so Next.js forwards server-side (no CORS).
 * Matches trek-studio-dev: `https://trek-studio-dev-app.azurewebsites.net/api/proxy/api/...`
 */
export function getBrowserApiBaseUrl(): string {
  const configured = (process.env.NEXT_PUBLIC_API_BASE_URL ?? "").trim();
  if (!configured) return "";

  if (typeof window === "undefined") {
    return configured.replace(/\/+$/, "");
  }

  if (!/^https?:\/\//i.test(configured)) {
    return configured.replace(/\/+$/, "");
  }

  try {
    const target = new URL(configured);
    if (target.origin !== window.location.origin) {
      return "/api/proxy";
    }
    return configured.replace(/\/+$/, "");
  } catch {
    return configured.replace(/\/+$/, "");
  }
}

/** JSON headers for proxied backend calls (no Authorization). */
export function getBrowserProxyHeaders(
  extra?: Record<string, string>
): Record<string, string> {
  return {
    "Content-Type": "application/json",
    ...extra,
  };
}

/**
 * Client request headers for backend API calls.
 * When proxied via `/api/proxy`, omits Authorization (session cookie only) to avoid HTTP 431.
 */
export function getClientAuthHeaders(
  accessToken?: string | null,
  extra?: Record<string, string>
): Record<string, string> {
  if (isBrowserApiProxied()) {
    return { ...extra };
  }
  if (!accessToken) {
    return { ...extra };
  }
  return {
    Authorization: `Bearer ${accessToken}`,
    ...extra,
  };
}

/** fetch() init with proxy-safe auth (credentials + no duplicate Bearer). */
export function getClientFetchInit(
  accessToken?: string | null,
  init?: RequestInit
): RequestInit {
  const extraHeaders: Record<string, string> = {};
  if (init?.headers) {
    if (init.headers instanceof Headers) {
      init.headers.forEach((value, key) => {
        extraHeaders[key] = value;
      });
    } else if (Array.isArray(init.headers)) {
      for (const [key, value] of init.headers) {
        extraHeaders[key] = value;
      }
    } else {
      Object.assign(extraHeaders, init.headers);
    }
  }

  const headers = getClientAuthHeaders(accessToken, extraHeaders);

  return {
    ...init,
    credentials: isBrowserApiProxied()
      ? (init?.credentials ?? "include")
      : init?.credentials,
    headers,
  };
}

/**
 * Normalize citation href to the underlying blob / SharePoint URL.
 * Markdown links may be `/api/download-citation?url=...`, proxied paths, or blob-* APIs.
 */
export function extractCitationBlobUrl(citationHref: string): string {
  if (typeof citationHref !== "string" || !citationHref.trim()) {
    return citationHref;
  }

  const decodeParam = (value: string | null): string | null => {
    if (!value) return null;
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  };

  const base =
    typeof window !== "undefined" ? window.location.origin : "http://localhost";

  let current = citationHref.trim();
  for (let depth = 0; depth < 6; depth++) {
    try {
      const parsed = new URL(current, base);

      if (/download-citation\/?$/i.test(parsed.pathname)) {
        const inner =
          decodeParam(parsed.searchParams.get("url")) ??
          decodeParam(parsed.searchParams.get("blob_url"));
        if (!inner || inner === current) break;
        current = inner;
        continue;
      }

      if (/\/blob-(?:view|download)-url\/?$/i.test(parsed.pathname)) {
        const blob = decodeParam(parsed.searchParams.get("blob_url"));
        if (!blob || blob === current) break;
        current = blob;
        continue;
      }
    } catch {
      break;
    }
    break;
  }

  return current;
}

/** True when we have a real remote file location (not just an API stub without url=). */
export function isResolvableCitationBlobUrl(url: string): boolean {
  const blob = extractCitationBlobUrl(url);
  return !!blob?.trim() && /^https?:\/\//i.test(blob);
}

/** True when the href targets the backend github-repo-zip download API. */
export function isGithubRepoZipApiUrl(url: string): boolean {
  return typeof url === "string" && url.toLowerCase().includes("github-repo-zip");
}

/**
 * Rewrite backend `/api/...` URLs to same-origin `/api/proxy/api/...`.
 * Markdown often embeds the full INTERNAL_API_BASE_URL host; direct fetch violates CSP.
 */
export function toProxiedBackendApiUrl(apiUrl: string): string {
  const trimmed = (apiUrl ?? "").trim();
  if (!trimmed || !isBrowserApiProxied()) {
    return trimmed;
  }
  if (trimmed.startsWith("/api/proxy/") || trimmed === "/api/proxy") {
    return trimmed;
  }

  const pageOrigin =
    typeof window !== "undefined" ? window.location.origin : "http://localhost";

  try {
    const parsed = new URL(trimmed, pageOrigin);
    if (!/^\/api\//i.test(parsed.pathname)) {
      return trimmed;
    }
    return `/api/proxy${parsed.pathname}${parsed.search}`;
  } catch {
    return trimmed;
  }
}

/** Fetch a github-repo-zip archive via the proxy when the app is proxied. */
export async function fetchGithubRepoZipBlob(
  apiUrl: string,
  accessToken?: string | null
): Promise<Blob> {
  const fetchUrl = toProxiedBackendApiUrl(apiUrl);
  const init = isBrowserApiProxied()
    ? getCitationFetchInit(accessToken)
    : getClientFetchInit(accessToken, {
        method: "GET",
        headers: getClientAuthHeaders(accessToken, { Accept: "*/*" }),
        redirect: "follow",
      });

  const response = await fetch(fetchUrl, init);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: response.statusText,
    }));
    const err = errorData as { error?: string; detail?: string };
    throw new Error(
      err.error || err.detail || `Download failed: ${response.statusText}`
    );
  }
  return response.blob();
}

/**
 * Citation download/preview URL.
 * Proxied app: `/api/proxy/api/download-citation` (Bearer attached server-side, same as other APIs).
 * Direct backend dev: `/api/download-citation` local route or absolute backend URL.
 */
export function getDownloadCitationUrl(
  citationHref: string,
  options?: { inline?: boolean }
): string {
  const blobUrl = extractCitationBlobUrl(citationHref);
  if (!isResolvableCitationBlobUrl(citationHref)) {
    throw new Error(
      "Citation link is missing the file URL (url= query parameter). The document cannot be downloaded."
    );
  }

  const inlineQ = options?.inline ? "&inline=1" : "";
  const query = `url=${encodeURIComponent(blobUrl)}${inlineQ}`;

  if (typeof window !== "undefined") {
    if (isBrowserApiProxied()) {
      return `/api/proxy/api/download-citation?${query}`;
    }
    return `/api/download-citation?${query}`;
  }

  const base = (process.env.NEXT_PUBLIC_API_BASE_URL ?? "").replace(/\/$/, "");
  if (isBrowserApiProxied()) {
    return `/api/proxy/api/download-citation?${query}`;
  }
  return `${base}/api/download-citation?${query}`;
}

/**
 * True when the browser may call backend APIs (Recent Chats, history, citations, etc.).
 * Proxied deployments attach Bearer server-side from the NextAuth cookie — no client token.
 */
export function isBackendApiSessionReady(
  session:
    | { user?: unknown; accessToken?: string | null; error?: string }
    | null
    | undefined
): boolean {
  if (!session?.user) return false;
  if (session.error === "RefreshAccessTokenError" && !isBrowserApiProxied()) {
    return false;
  }
  if (isBrowserApiProxied()) return true;
  return !!session.accessToken;
}

/** True when the user can request citations (cookie-only when proxied). */
export function isCitationSessionReady(
  session:
    | { user?: unknown; accessToken?: string | null; error?: string }
    | null
    | undefined
): boolean {
  return isBackendApiSessionReady(session);
}

/** Same as other proxied APIs: session cookie only, no client Authorization (avoids HTTP 431). */
export function getCitationFetchInit(accessToken?: string | null): RequestInit {
  return getClientFetchInit(accessToken, {
    method: "GET",
    headers: { Accept: "*/*" },
    redirect: "follow",
  });
}

/** API path for blob view URLs (Office embed / open-in-tab). */
export function getBlobViewUrlApiPath(blobUrl: string): string {
  const query = `blob_url=${encodeURIComponent(blobUrl)}`;
  if (isBrowserApiProxied()) {
    return `/api/proxy/api/v1/rfp/blob-view-url?${query}`;
  }
  const base = (process.env.NEXT_PUBLIC_API_BASE_URL ?? "").replace(/\/$/, "");
  return `${base}/api/v1/rfp/blob-view-url?${query}`;
}

/** API path to resolve a SharePoint / blob path to a short-lived download URL. */
export function getBlobDownloadUrlApiPath(blobUrl: string): string {
  const query = `blob_url=${encodeURIComponent(blobUrl)}`;
  if (isBrowserApiProxied()) {
    return `/api/proxy/api/v1/rfp/blob-download-url?${query}`;
  }
  const base = (process.env.NEXT_PUBLIC_API_BASE_URL ?? "").replace(/\/$/, "");
  return `${base}/api/v1/rfp/blob-download-url?${query}`;
}

/** Resolve download_url via backend (works for SharePoint paths; used before streaming bytes). */
export async function fetchBlobDownloadUrl(
  sourceBlobUrl: string,
  accessToken?: string | null
): Promise<{ download_url?: string } | null> {
  if (!isResolvableCitationBlobUrl(sourceBlobUrl)) return null;
  const blobUrl = extractCitationBlobUrl(sourceBlobUrl);
  try {
    const response = await fetch(
      getBlobDownloadUrlApiPath(blobUrl),
      isBrowserApiProxied()
        ? getCitationFetchInit(accessToken)
        : {
            method: "GET",
            headers: {
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
              Accept: "application/json",
            },
          }
    );
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

/** Fetch bytes from a signed SharePoint download URL (browser trust store; no auth header). */
export async function fetchSharePointFileAsBlob(downloadUrl: string): Promise<Blob> {
  const response = await fetch(downloadUrl, {
    method: "GET",
    credentials: "omit",
    redirect: "follow",
  });
  if (!response.ok) {
    const detail = await response.text().catch(() => response.statusText);
    throw new Error(detail || `Failed to fetch file: ${response.statusText}`);
  }
  return response.blob();
}

/** Trigger a file save in the browser from an in-memory blob. */
export function downloadBlobInBrowser(blob: Blob, fileName: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName || "download";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/** Resolve a short-lived download URL — never use viewUrl for saves. */
export async function resolveCitationDownloadUrl(
  citationHref: string,
  accessToken?: string | null
): Promise<string> {
  const data = await fetchBlobDownloadUrl(citationHref, accessToken);
  const downloadUrl = data?.download_url;
  if (!downloadUrl) {
    throw new Error("Could not resolve download link for this citation.");
  }
  return downloadUrl;
}

/**
 * Fetch citation file bytes via `/api/proxy/api/download-citation` when proxied.
 * Bearer is attached on the server from the NextAuth session (same as chat/API).
 */
export async function fetchCitationFileBlob(
  citationHref: string,
  accessToken?: string | null,
  options?: { inline?: boolean }
): Promise<Blob> {
  if (!isResolvableCitationBlobUrl(citationHref)) {
    throw new Error(
      "Citation link is missing the file URL. The backend may not have attached the document path."
    );
  }

  let signedUrlFollowed = false;

  const fetchFromProxy = async (requestUrl: string): Promise<Blob> => {
    const proxyResp = await fetch(requestUrl, getCitationFetchInit(accessToken));
    if (!proxyResp.ok) {
      const errBody = await proxyResp.json().catch(() => ({}));
      const detail =
        (errBody as { detail?: string })?.detail ||
        (errBody as { error?: string })?.error ||
        proxyResp.statusText;
      const err = new Error(detail || "Failed to fetch document");
      (err as Error & { status?: number }).status = proxyResp.status;
      throw err;
    }

    const contentType = proxyResp.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      const json = (await proxyResp.json()) as { download_url?: string; error?: string };
      if (json?.error) {
        throw new Error(json.error);
      }
      // SharePoint / signed URL — browser fetch (uses OS cert store, no server TLS).
      if (json?.download_url && !signedUrlFollowed) {
        signedUrlFollowed = true;
        return fetchSharePointFileAsBlob(json.download_url);
      }
      throw new Error("Server did not return file bytes or a download URL.");
    }

    return proxyResp.blob();
  };

  try {
    return await fetchFromProxy(
      getDownloadCitationUrl(citationHref, { inline: options?.inline })
    );
  } catch (err) {
    throw err instanceof Error ? err : new Error(String(err));
  }
}

export function isTrustedSharePointUrl(urlString: string): boolean {
  if (!urlString) return false;
  try {
    const parsed = new URL(
      urlString,
      typeof window !== "undefined" ? window.location.origin : "http://localhost"
    );
    const hostname = parsed.hostname.toLowerCase();
    return hostname === "sharepoint.com" || hostname.endsWith(".sharepoint.com");
  } catch {
    return false;
  }
}

/** Build SharePoint Office Online embed URL (Claude-style in-panel preview). */
export function buildOfficeEmbedUrl(
  url: string,
  variant: "pptx" | "xlsx"
): string | null {
  if (!isTrustedSharePointUrl(url)) return null;
  try {
    const spUrl = new URL(url);
    spUrl.searchParams.set("action", "embedview");
    spUrl.searchParams.delete("mobileredirect");
    if (variant === "pptx") {
      spUrl.searchParams.set("wdHideToolbar", "False");
      spUrl.searchParams.set("wdHideGrip", "True");
      spUrl.searchParams.set("wdDownload", "0");
      spUrl.searchParams.set("wdPrint", "0");
      spUrl.searchParams.set("wdAr", "16:9");
    } else {
      spUrl.searchParams.set("wdHideGridlines", "True");
      spUrl.searchParams.set("wdHideHeaders", "True");
      spUrl.searchParams.set("wdDownload", "0");
      spUrl.searchParams.set("wdPrint", "0");
      spUrl.searchParams.set("wdInConfigurator", "True");
    }
    return spUrl.toString();
  } catch {
    return null;
  }
}

/** Resolve view_url from backend blob-view-url API (used for preview, not full download). */
export async function fetchBlobViewUrl(
  sourceBlobUrl: string,
  accessToken?: string | null
): Promise<{ view_url?: string; viewable_url?: string } | null> {
  try {
    const response = await fetch(
      getBlobViewUrlApiPath(sourceBlobUrl),
      isBrowserApiProxied()
        ? getCitationFetchInit(accessToken)
        : {
            method: "GET",
            headers: {
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
              Accept: "application/json",
            },
          }
    );
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * Claude-style Office preview: blob-view-url → SharePoint embed iframe.
 * Does not download the full file; use download-citation only for the Download button.
 */
export async function resolveOfficeCitationPreview(
  href: string,
  variant: "pptx" | "xlsx",
  accessToken?: string | null
): Promise<{ officeEmbedUrl: string | null; viewUrl: string | null }> {
  const sourceBlobUrl = extractCitationBlobUrl(href);
  let officeEmbedUrl: string | null = null;

  if (isTrustedSharePointUrl(sourceBlobUrl) && sourceBlobUrl.includes("Doc.aspx")) {
    officeEmbedUrl = buildOfficeEmbedUrl(sourceBlobUrl, variant);
  }

  const viewData = await fetchBlobViewUrl(sourceBlobUrl, accessToken);
  const viewUrl = viewData?.view_url || viewData?.viewable_url || null;

  if (!officeEmbedUrl && viewUrl) {
    officeEmbedUrl = buildOfficeEmbedUrl(viewUrl, variant);
  }

  return { officeEmbedUrl, viewUrl };
}

/**
 * PDF embed URL for SharePoint (same idea as PPTX embedview — uses your M365 session).
 * Do NOT use view.officeapps.live.com with private SharePoint paths; Office servers
 * cannot access them and show "File not found".
 */
export function buildPdfEmbedUrl(url: string): string | null {
  if (!url || !/^https?:\/\//i.test(url)) return null;
  if (!isTrustedSharePointUrl(url)) return null;

  try {
    const lower = url.toLowerCase();

    if (lower.includes("wopiframe.aspx") || lower.includes("/embed.aspx")) {
      return url;
    }

    if (url.includes("Doc.aspx")) {
      const spUrl = new URL(url);
      spUrl.searchParams.set("action", "embedview");
      spUrl.searchParams.delete("mobileredirect");
      spUrl.searchParams.set("wdDownload", "0");
      spUrl.searchParams.set("wdPrint", "0");
      return spUrl.toString();
    }
  } catch {
    return null;
  }

  return null;
}

/**
 * PDF preview via blob-view-url → SharePoint iframe (like PPTX).
 * Uses backend view_url in iframe with the user's SharePoint session — not Office Online public embed.
 */
export async function resolvePdfCitationPreview(
  href: string,
  accessToken?: string | null
): Promise<{ officeEmbedUrl: string | null; viewUrl: string | null }> {
  const sourceBlobUrl = extractCitationBlobUrl(href);

  const viewData = await fetchBlobViewUrl(sourceBlobUrl, accessToken);
  const viewUrl = viewData?.view_url || viewData?.viewable_url || null;

  if (viewUrl && isTrustedSharePointUrl(viewUrl)) {
    const officeEmbedUrl = buildPdfEmbedUrl(viewUrl) ?? viewUrl;
    return { officeEmbedUrl, viewUrl };
  }

  const officeEmbedUrl = buildPdfEmbedUrl(sourceBlobUrl);
  return { officeEmbedUrl, viewUrl };
}
